import { AxiosError, isAxiosError } from "axios";

type ErrorWithStatus<StatusCode extends number> = AxiosError<
  unknown,
  unknown
> & {
  response: { status: StatusCode };
};

type NetworkError = AxiosError & { response: undefined };

function isAxiosErrorWithStatus<S extends number>(
  error: unknown,
  status: S,
): error is ErrorWithStatus<S> {
  return isAxiosError(error) && error.response?.status === status;
}

/**
 * Returns the HTTP status from an Axios error response, if one exists.
 */
export function getHttpStatus(error: unknown): number | undefined {
  if (!isAxiosError(error)) return undefined;
  return error.response?.status;
}

/**
 * Returns true if the error is a 404 Not Found error.
 */
export function is404Error(error: unknown): error is ErrorWithStatus<404> {
  return isAxiosErrorWithStatus(error, 404);
}

/**
 * Returns true if the error is a 400 Bad Request error.
 */
export function is400Error(error: unknown): error is ErrorWithStatus<400> {
  return isAxiosErrorWithStatus(error, 400);
}

/**
 * Returns true if the error is a 401 Unauthorized error.
 */
export function is401Error(error: unknown): error is ErrorWithStatus<401> {
  return isAxiosErrorWithStatus(error, 401);
}

/**
 * Returns true if the error is a 403 Forbidden error.
 */
export function is403Error(error: unknown): error is ErrorWithStatus<403> {
  return isAxiosErrorWithStatus(error, 403);
}

/**
 * Returns true if the error is a 409 Conflict error.
 */
export function is409Error(error: unknown): error is ErrorWithStatus<409> {
  return isAxiosErrorWithStatus(error, 409);
}

/**
 * Returns true if the error is a network error (i.e. the request was made
 * but no response was received). Common causes include no internet connection,
 * DNS failure, connection timeout, or a server that hard-closed the connection.
 *
 * Contrast with HTTP errors (4xx, 5xx), where a response was received and
 * `error.response` is set.
 */
export function isNetworkError(error: unknown): error is NetworkError {
  if (!isAxiosError(error)) return false;
  return !error.response;
}

/**
 * Returns true if the error is an HTTP error — an Axios error where a response
 * was received (any status code). Returns false for network errors (no response)
 * and for non-Axios errors.
 */
export function isHttpError(error: unknown): error is ErrorWithStatus<number> {
  return isAxiosError(error) && error.response != null;
}

/**
 * Extracts the server-provided message from an error's `response.data.message`
 * (the shape returned by the API's error handler: `{ code, message }`).
 *
 * Deliberately returns `null` rather than falling back to the Axios
 * `Error.message` — that fallback is the opaque "Request failed with status
 * code 400" string, which is not useful to surface to a user. Callers should
 * pair this with their own user-facing fallback copy.
 */
export function getServerErrorMessage(error: unknown): string | null {
  const data = (error as { response?: { data?: unknown } } | null)?.response
    ?.data;

  if (data && typeof data === "object" && "message" in data) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message.trim();
  }

  return null;
}
